import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CHAT_REACTION_CONFIG, CHAT_REACTION_CONFIG_MAP } from '../shared/chatReactionConfig';
import '../../../../css/common/chat/ChatMessageReactions.css';

const REACTION_PICKER_OPEN_EVENT = 'chat-reaction-picker-open';
const REACTION_PICKER_VISIBILITY_EVENT = 'chat-reaction-picker-visibility-change';
const REACTION_PICKER_VIEWPORT_PADDING = 8;
const REACTION_PICKER_ANCHOR_GAP = 6;
const REACTION_PICKER_FALLBACK_WIDTH = 166;
const REACTION_PICKER_FALLBACK_HEIGHT = 48;

const stopEvent = (event) => {
  event.preventDefault();
  event.stopPropagation();
};

const ChatMessageReactionBar = ({
  messageId = '',
  reactions = [],
  canReact = false,
  onToggleReaction = null,
  onOpenDetail = null,
  isPending = null,
  showReactionChips = true,
  showAddButton = true,
  compact = false,
}) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerStyle, setPickerStyle] = useState(null);
  const wrapperRef = useRef(null);
  const addButtonRef = useRef(null);
  const pickerRef = useRef(null);
  const normalizedMessageId = String(messageId || '');

  // 集計は種別ごとの数字を出さず「使用されたリアクションを少し重ねた束＋合計数」で表示する。
  // タップ=一覧モーダル。自分のリアクションの追加/切替/解除はピッカー側が担う。
  const visibleReactions = useMemo(
    () => (Array.isArray(reactions) ? reactions : [])
      .filter((reaction) => Number(reaction?.count) > 0)
      .slice()
      .sort((a, b) => Number(b?.count || 0) - Number(a?.count || 0)),
    [reactions],
  );
  const totalReactionCount = useMemo(
    () => visibleReactions.reduce((sum, reaction) => sum + Number(reaction?.count || 0), 0),
    [visibleReactions],
  );
  const reactedByMe = visibleReactions.some((reaction) => reaction?.reactedByMe);

  const handleClusterClick = (event) => {
    stopEvent(event);
    if (typeof onOpenDetail === 'function' && visibleReactions.length > 0) {
      onOpenDetail(messageId, visibleReactions[0].type);
    }
  };

  const updatePickerPosition = useCallback(() => {
    const triggerRect = addButtonRef.current?.getBoundingClientRect();
    if (!triggerRect || typeof window === 'undefined') return;
    const pickerRect = pickerRef.current?.getBoundingClientRect();
    const pickerWidth = pickerRect?.width || REACTION_PICKER_FALLBACK_WIDTH;
    const pickerHeight = pickerRect?.height || REACTION_PICKER_FALLBACK_HEIGHT;
    const maxLeft = Math.max(
      REACTION_PICKER_VIEWPORT_PADDING,
      window.innerWidth - pickerWidth - REACTION_PICKER_VIEWPORT_PADDING,
    );
    const left = Math.max(
      REACTION_PICKER_VIEWPORT_PADDING,
      Math.min(triggerRect.left, maxLeft),
    );
    const belowTop = triggerRect.bottom + REACTION_PICKER_ANCHOR_GAP;
    const aboveTop = triggerRect.top - pickerHeight - REACTION_PICKER_ANCHOR_GAP;
    const hasRoomBelow = belowTop + pickerHeight <= window.innerHeight - REACTION_PICKER_VIEWPORT_PADDING;
    const top = hasRoomBelow
      ? belowTop
      : Math.max(REACTION_PICKER_VIEWPORT_PADDING, aboveTop);
    setPickerStyle({
      position: 'fixed',
      left: `${left}px`,
      top: `${top}px`,
      visibility: 'visible',
    });
  }, []);

  useLayoutEffect(() => {
    if (!pickerOpen) return;
    updatePickerPosition();
  }, [pickerOpen, updatePickerPosition]);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper || !normalizedMessageId) return undefined;
    wrapper.dispatchEvent(
      new CustomEvent(REACTION_PICKER_VISIBILITY_EVENT, {
        bubbles: true,
        detail: {
          messageId: normalizedMessageId,
          open: pickerOpen,
        },
      })
    );
    return undefined;
  }, [normalizedMessageId, pickerOpen]);

  useEffect(() => {
    if (!pickerOpen) return undefined;
    const handlePointerDown = (event) => {
      if (wrapperRef.current?.contains(event.target)) return;
      const portalPicker = event.target?.closest?.('[data-chat-reaction-picker-owner]');
      if (portalPicker?.dataset?.chatReactionPickerOwner === normalizedMessageId) return;
      setPickerOpen(false);
    };
    const handleEscape = (event) => {
      if (event.key !== 'Escape') return;
      setPickerOpen(false);
      addButtonRef.current?.focus();
    };
    const handleReposition = () => updatePickerPosition();
    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [normalizedMessageId, pickerOpen, updatePickerPosition]);

  useEffect(() => {
    const handleAnotherPickerOpen = (event) => {
      const openedMessageId = String(event?.detail?.messageId || '');
      if (!openedMessageId || openedMessageId === normalizedMessageId) return;
      setPickerOpen(false);
    };
    document.addEventListener(REACTION_PICKER_OPEN_EVENT, handleAnotherPickerOpen);
    return () => document.removeEventListener(REACTION_PICKER_OPEN_EVENT, handleAnotherPickerOpen);
  }, [normalizedMessageId]);

  const shouldShowChips = showReactionChips && visibleReactions.length > 0;
  const shouldShowAddButton = showAddButton && canReact;
  if (!shouldShowChips && !shouldShowAddButton) return null;

  return (
    <div
      ref={wrapperRef}
      className={[
        'chat-message-reaction-bar',
        compact ? 'chat-message-reaction-bar--compact' : '',
      ].filter(Boolean).join(' ')}
    >
      {shouldShowChips ? (
        <button
          type="button"
          className={[
            'chat-message-reaction-cluster',
            reactedByMe ? 'chat-message-reaction-cluster--active' : '',
          ].filter(Boolean).join(' ')}
          onClick={handleClusterClick}
          aria-label={`リアクション一覧を開く（合計${totalReactionCount}件）`}
          title="リアクション一覧"
        >
          <span className="chat-message-reaction-cluster__icons" aria-hidden="true">
            {visibleReactions.map((reaction, index) => {
              const config = CHAT_REACTION_CONFIG_MAP[reaction.type] || CHAT_REACTION_CONFIG[0];
              return (
                <span
                  key={`${messageId}:${reaction.type}`}
                  className="chat-message-reaction-cluster__icon"
                  data-reaction-type={reaction.type}
                  style={{ zIndex: visibleReactions.length - index }}
                >
                  <i className={config.iconClassName} aria-hidden="true"></i>
                </span>
              );
            })}
          </span>
          <span className="chat-message-reaction-cluster__count">{totalReactionCount}</span>
        </button>
      ) : null}

      {shouldShowAddButton ? (
        <div className="chat-message-reaction-bar__pickerWrap">
          <button
            ref={addButtonRef}
            type="button"
            className={[
              'chat-message-reaction-add',
              compact ? 'chat-message-reaction-add--compact' : '',
            ].filter(Boolean).join(' ')}
            onClick={(event) => {
              stopEvent(event);
              setPickerOpen((prev) => {
                const next = !prev;
                if (next && normalizedMessageId) {
                  setPickerStyle(null);
                  document.dispatchEvent(
                    new CustomEvent(REACTION_PICKER_OPEN_EVENT, {
                      detail: { messageId: normalizedMessageId },
                    })
                  );
                }
                return next;
              });
            }}
            aria-label="リアクションを追加"
            title="リアクションを追加"
          >
            <i className="fa-solid fa-face-smile" aria-hidden="true"></i>
          </button>
          {pickerOpen && typeof document !== 'undefined' ? createPortal(
            <div
              ref={pickerRef}
              className="chat-message-reaction-picker"
              style={pickerStyle || {
                position: 'fixed',
                left: 0,
                top: 0,
                visibility: 'hidden',
              }}
              role="group"
              aria-label="リアクションを選択"
              data-chat-reaction-picker-owner={normalizedMessageId}
              onClick={(event) => event.stopPropagation()}
            >
              {CHAT_REACTION_CONFIG.map((config) => (
                <button
                  key={`${messageId}:${config.type}`}
                  type="button"
                  className="chat-message-reaction-picker__item"
                  data-reaction-type={config.type}
                  onClick={async (event) => {
                    stopEvent(event);
                    if (typeof onToggleReaction === 'function') {
                      await onToggleReaction(messageId, config.type);
                    }
                    setPickerOpen(false);
                  }}
                  disabled={typeof isPending === 'function' ? isPending(messageId, config.type) : false}
                  aria-label={config.label}
                  title={config.label}
                >
                  <i className={config.iconClassName} aria-hidden="true"></i>
                </button>
              ))}
            </div>,
            document.body,
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default ChatMessageReactionBar;
